import { AfterContentInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ValidationLimits, Validators, ValidatorKeys } from '@sb/library/base';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ItemWithID } from '@sb/library/base';
import { Observable } from 'rxjs/internal/Observable';
import { map, startWith } from 'rxjs/operators';
import { FormFieldSelectBaseComponent } from '../form-field-select-base.component';
import { FormFieldSelectItemComponent, IItemGroup } from '../form-field-select-item.component';

@Component({
    selector: 'lib-input-select-autocomplete',
    templateUrl: './form-field-select-autocomplete.component.html',
    styleUrls: ['./form-field-select-autocomplete.component.scss']
})
export class FormFieldSelectAutoCompleteComponent<T extends ItemWithID | string> extends FormFieldSelectBaseComponent<T, T | null> implements AfterContentInit {
    @ViewChild(MatAutocompleteTrigger, { static: false }) autocomplete!: MatAutocompleteTrigger;
    @ViewChild('input', { static: false }) input?: ElementRef;

    @Input() allowCreation: boolean = true;

    private _filteredItemsByGroup!: Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>>;
    private _selectedItem!: FormFieldSelectItemComponent<T>;
    private _remainingItemsByGroup!: Map<IItemGroup | null, Set<FormFieldSelectItemComponent<T>>>;

    override ngAfterContentInit(): void {
        super.ngAfterContentInit();
        this.viewControl.AddDefaultValidators(Validators.maxLength(ValidationLimits.Text.MaxLength));
        // Propagate maxlength errors from the view control to the form control so the container shows them.
        this.viewControl.statusChanges.subscribe(() => {
            const viewControlErrors = this.viewControl.errors;
            const formControlErrors = this.formControl.errors ?? {};

            // Use the canonical validator key for maxlength and keep the shape the validator returns
            if (viewControlErrors?.[ValidatorKeys.maxlength]) {
                formControlErrors[ValidatorKeys.maxlength] = viewControlErrors[ValidatorKeys.maxlength];
            } else {
                delete formControlErrors[ValidatorKeys.maxlength];
            }


            const currentErrors = this.formControl.errors
            const mergedErrors = { ...currentErrors, ...formControlErrors };
            this.formControl.setErrors(Object.keys(mergedErrors).length ? mergedErrors : null);
        });

        if (!this.initialized) {
            // we have no itemquery list but we still need to subscribe to value changes
            this.SubscribeToViewValueChanges();
        }
    }

    override SortItems() {
        super.SortItems();

        this.viewControl.setValue(this.GetViewControlValue(this.formControl.value));
        this._remainingItemsByGroup = new Map();
        if (this.formControl.value) {
            this._selectedItem = this.itemMap.get(this.identifierOf(this.formControl.value!))!;
        } else {
            this.sortedItemListByGroup.forEach((value, key) => {
                this._remainingItemsByGroup.set(key, new Set(value));
            });
        }
        this.SubscribeToViewValueChanges();
    }

    override writeValue(value: T | null) {
        setTimeout(() => {
            if (this.nameOf) {
                this.viewControl.setValue(value !== null ? this.nameOf(value) : '');
            } else if (typeof value === 'string') {
                this.viewControl.setValue(value);
            }
        });
    }

    Selected(event: MatAutocompleteSelectedEvent) {
        const item = this.itemMap.get(event.option.value)!;
        this._remainingItemsByGroup.get(item.group)!.delete(item);
        this._selectedItem = item;
        this.UpdateFormControl();
    }

    Clicked(event: any) {
        if (!this.isDisabled) {
            event.preventDefault();
            this.input?.nativeElement?.focus();
            this.autocomplete.openPanel();
        }
    }

    PanelClosed(event: any) {
        this.formControl.markAsTouched();
    }

    private SubscribeToViewValueChanges(): void {
        this._filteredItemsByGroup = this.viewControl.valueChanges.pipe(
            startWith(
                this.formControl.value && this.nameOf
                    ? this.nameOf!(this.formControl.value)
                    : typeof this.formControl.value === 'string'
                      ? this.formControl.value
                      : ''
            ),
            map((value: string) => {
                if (this.allowCreation) {
                    this._selectedItem = new FormFieldSelectItemComponent();
                    this._selectedItem.value = value as T;

                    this.SetFormControlValue(this._selectedItem.value);
                }

                this.OnBlur();

                const items = value ? this.Filter(value) : this.allRemainingItems;
                if (items.length > 2000) {
                    // Limit autocomplete list to avoid performance degradation
                    items.length = 2000;
                }

                this._remainingItemsByGroup = new Map();
                const itemsByGroup = new Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>();

                this.sortedItemListByGroup?.forEach((items, group) => {
                    this._remainingItemsByGroup.set(group, new Set(items.filter(item => this.identifierOf(item.value) !== value)));
                    itemsByGroup.set(group, []);
                });

                items.forEach(item => {
                    itemsByGroup.get(item.group)!.push(item);
                });

                this.formControl.markAsUntouched();

                return itemsByGroup;
            })
        );
    }

    private Filter(value: string): FormFieldSelectItemComponent<T>[] {
        const checkValue = value.trim().toLowerCase();
        return this.allRemainingItems.filter(item => this.nameOf!(item.value)?.toLowerCase().includes(checkValue));
    }

    private UpdateFormControl(): void {
        this.viewControl.setValue(this.nameOf!(this._selectedItem.value));
        this.SetFormControlValue(this._selectedItem.value);

        this.OnBlur();
    }

    private GetViewControlValue(value: T | null): string {
        if (!value) {
            return '';
        }
        return typeof value === 'string' ? value : this.nameOf!(value);
    }

    private get allRemainingItems(): FormFieldSelectItemComponent<T>[] {
        const result: FormFieldSelectItemComponent<T>[] = [];

        this._remainingItemsByGroup?.forEach(value => {
            result.push(...[...value]);
        });

        return result;
    }

    get filteredItemsByGroup(): Observable<Map<IItemGroup | null, FormFieldSelectItemComponent<T>[]>> {
        return this._filteredItemsByGroup;
    }
}
